'use client';
import { motion } from 'framer-motion';
import { FaWhatsapp, FaRocket, FaClock, FaMobile, FaGlobe, FaDatabase, FaWifi, FaCloud } from 'react-icons/fa';
import { profile } from '@/data/profile';
import OrbitalObject from '../ui/OrbitalObject';

const services = [
    {
        id: 1,
        title: "30-Day MVP Development",
        description: "Get your Minimum Viable Product built and launched in just 30 days. Perfect for startups and entrepreneurs.",
        features: [
            "Full-stack web application",
            "Responsive design",
            "User authentication",
            "Database integration",
            "API development",
            "Analytics dashboard"
        ],
        icon: FaRocket,
        price: "Starting from $2,999",
        timeline: "30 days",
        popular: true
    },
    {
        id: 2,
        title: "Mobile App Development",
        description: "Cross-platform mobile applications using React Native and Flutter. One codebase, multiple platforms.",
        features: [
            "iOS & Android compatibility",
            "Native performance",
            "Push notifications",
            "Offline functionality",
            "App store deployment",
            "Maintenance & updates"
        ],
        icon: FaMobile,
        price: "Starting from $4,999",
        timeline: "45-60 days",
        popular: false
    },
    {
        id: 3,
        title: "Web Application Development",
        description: "Custom web applications built with modern technologies like React, Next.js, and Node.js.",
        features: [
            "Modern UI/UX design",
            "SEO optimization",
            "Performance optimization",
            "Security implementation",
            "Cloud deployment",
            "Ongoing support"
        ],
        icon: FaGlobe,
        price: "Starting from $3,999",
        timeline: "30-45 days",
        popular: false
    },
    {
        id: 4,
        title: "AI/ML Integration",
        description: "Integrate artificial intelligence and machine learning capabilities into your existing applications.",
        features: [
            "Custom ML models",
            "Data analysis & insights",
            "Automation solutions",
            "Chatbot development",
            "Predictive analytics",
            "AI-powered features"
        ],
        icon: FaDatabase,
        price: "Starting from $5,999",
        timeline: "60-90 days",
        popular: false
    },
    {
        id: 5,
        title: "IoT Projects",
        description: "Build connected IoT solutions with sensor integration, real-time data processing, and cloud connectivity.",
        features: [
            "Sensor integration",
            "Device connectivity",
            "Real-time monitoring",
            "Cloud platform integration",
            "Data analytics dashboard",
            "Remote control & automation"
        ],
        icon: FaWifi,
        price: "Starting from $6,999",
        timeline: "60-90 days",
        popular: false
    },
    {
        id: 6,
        title: "IaaS + SaaS Package",
        description: "A unified infrastructure and software solution for startups and growing businesses in a single package.",
        features: [
            "Cloud infrastructure setup",
            "Managed software deployment",
            "Scalable hosting and storage",
            "Automated updates and maintenance",
            "Integrated security and backups",
            "Usage-based optimization"
        ],
        icon: FaCloud,
        price: "Starting from $5,499",
        timeline: "45-60 days",
        popular: false
    }
];

export default function Services() {
    const container = {
        hidden: { opacity: 0 },
        show: {
            opacity: 1,
            transition: {
                staggerChildren: 0.1
            }
        }
    };

    const item = {
        hidden: { opacity: 0, y: 20 },
        show: { opacity: 1, y: 0 }
    };

    const handleWhatsAppClick = (serviceName: string) => {
        const message = `Hi! I'm interested in your ${serviceName} service. Can you provide more details?`;
        const whatsappUrl = `https://wa.me/${profile.contact.whatsapp.replace(/[^\d]/g, '')}?text=${encodeURIComponent(message)}`;
        window.open(whatsappUrl, '_blank');
    };

    // Create 20 orbital objects with varying properties
    const orbitalConfigs = Array.from({ length: 20 }, (_, index) => ({
        color: [
            "#4f83ff", "#83ff4f", "#ff4f83", "#ff834f", "#834fff",
            "#4fff83", "#ff4fff", "#ffff4f", "#4fffff", "#ffffff",
            "#ff6b6b", "#4ecdc4", "#45b7d1", "#96ceb4", "#feca57",
            "#ff9ff3", "#54a0ff", "#5f27cd", "#00d2d3", "#ff9f43"
        ][index % 20],
        size: 4 + (index % 8) + 2, // Sizes from 6 to 13
        orbitDuration: 15 + (index % 25) + 5, // Duration from 20 to 44 seconds
        orbitRadius: 80 + (index % 15) * 20, // Radius from 80 to 360
        delay: (index % 10) * 2, // Delays from 0 to 18 seconds
    }));

    return (
        <section id="services" className="py-12 bg-black relative overflow-hidden min-h-screen flex items-center">
            {/* Map through 20 orbital objects */}
            {orbitalConfigs.map((config, index) => (
                <div key={index} className="absolute inset-0" style={{ left: '50%', top: '50%' }}>
                    <OrbitalObject
                        color={config.color}
                        size={config.size}
                        orbitDuration={config.orbitDuration}
                        orbitRadius={config.orbitRadius}
                        delay={config.delay}
                    />
                </div>
            ))}

            <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10 w-full">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    className="text-center mb-8"
                >
                    <h2 className="text-3xl font-bold text-white mb-4">My Services</h2>
                    <p className="text-lg text-gray-400 max-w-2xl mx-auto">
                        From rapid MVP development to complex AI integrations
                    </p>
                </motion.div>

                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true }}
                    className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8 max-w-8xl mx-auto"
                >
                    {services.map((service) => {
                        const IconComponent = service.icon;
                        return (
                            <motion.div
                                key={service.id}
                                variants={item}
                                className="bg-zinc-800/80 backdrop-blur-sm p-5 rounded-lg shadow-md hover:shadow-lg transition-shadow border border-zinc-700 relative group h-full flex flex-col"
                                whileHover={{ scale: 1.02 }}
                            >
                                {service.popular && (
                                    <div className="absolute -top-3 left-4 bg-blue-500 text-white px-3 py-1 rounded-full text-xs font-semibold">
                                        Most Popular
                                    </div>
                                )}

                                <div className="flex items-center gap-3 mb-4">
                                    <div className="bg-zinc-700/80 p-2 rounded-lg">
                                        <IconComponent className="text-lg text-blue-300" />
                                    </div>
                                </div>

                                <h3 className="text-lg font-semibold mb-3 text-white">{service.title}</h3>

                                <p className="text-gray-300 text-sm mb-4 leading-relaxed flex-grow">{service.description}</p>

                                <div className="mb-4">
                                    <h4 className="text-white font-semibold mb-2 text-sm">What&apos;s Included:</h4>
                                    <div className="flex flex-wrap gap-1">
                                        {service.features.map((feature, index) => (
                                            <motion.span
                                                key={index}
                                                whileHover={{ scale: 1.05 }}
                                                className="px-2 py-1 bg-zinc-700/80 text-blue-300 rounded-full text-xs backdrop-blur-sm"
                                            >
                                                {feature}
                                            </motion.span>
                                        ))}
                                    </div>
                                </div>

                                <div className="mb-4 pt-3 border-t border-white/10">
                                    <div className="text-lg font-bold text-white">{service.price}</div>
                                    <div className="text-gray-400 flex items-center gap-1 text-sm">
                                        <FaClock className="text-xs" />
                                        {service.timeline}
                                    </div>
                                </div>

                                <motion.button
                                    type="button"
                                    whileHover={{ scale: 1.05, y: -2 }}
                                    whileTap={{ scale: 0.98 }}
                                    onClick={() => handleWhatsAppClick(service.title)}
                                    className="relative w-full bg-zinc-700/50 text-gray-300 hover:text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm mt-auto border border-zinc-600 hover:border-green-500/40 group overflow-hidden z-10"
                                    style={{ cursor: 'pointer' }}
                                >
                                    {/* Gradient background overlay */}
                                    <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                                    <div className="relative z-10 flex items-center gap-2">
                                        <FaWhatsapp className="text-lg group-hover:scale-110 transition-transform" />
                                        Get Started
                                    </div>
                                </motion.button>

                                <div className="absolute inset-0 bg-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity rounded-lg pointer-events-none" />
                            </motion.div>
                        );
                    })}
                </motion.div>

                {/* Call to Action Section */}
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6, delay: 0.3 }}
                    viewport={{ once: true }}
                    className="text-center mt-12 bg-zinc-800/80 backdrop-blur-sm border border-zinc-700 rounded-lg p-8 max-w-8xl mx-auto"
                >
                    <h3 className="text-2xl font-bold text-white mb-3">
                        Ready to Build Your Next Big Thing?
                    </h3>
                    <p className="text-lg text-gray-300 mb-4 max-w-2xl mx-auto">
                        Let&apos;s discuss your project and see how I can help you achieve your goals.
                    </p>
                    <motion.button
                        type="button"
                        whileHover={{ scale: 1.05, y: -3 }}
                        whileTap={{ scale: 0.95 }}
                        onClick={() => handleWhatsAppClick("Free Consultation")}
                        className="relative bg-zinc-700/50 text-gray-300 hover:text-white font-semibold py-3 px-6 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 mx-auto border border-zinc-600 hover:border-green-500/40 group overflow-hidden z-10"
                        style={{ cursor: 'pointer' }}
                    >
                        {/* Gradient background overlay */}
                        <div className="absolute inset-0 bg-gradient-to-br from-green-500 to-emerald-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                        <div className="relative z-10 flex items-center gap-2">
                            <FaWhatsapp className="text-xl group-hover:scale-110 transition-transform" />
                            Start Free Consultation
                        </div>
                    </motion.button>
                </motion.div>
            </div>
        </section>
    );
}